// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';

const logger = new Logger('LinuxScreenShareAudio');
const VIRTMIC_DEVICE_LABEL = 'fluxer-screen-share';
const VIRTMIC_DEVICE_DESCRIPTION = 'fluxer screen share audio';
const VIRTMIC_SAMPLE_RATE = 48000;
let patched = false;
let armedToken: symbol | null = null;
function isLinuxDesktop(): boolean {
	const electronApi = getElectronAPI();
	return Boolean(electronApi) && electronApi?.platform === 'linux';
}
function isVirtmicDeviceLabel(label: string | undefined | null): boolean {
	if (!label) return false;
	const normalised = label.trim().toLowerCase();
	return normalised.includes(VIRTMIC_DEVICE_LABEL) || normalised.includes(VIRTMIC_DEVICE_DESCRIPTION);
}
async function findVirtmicDeviceId(): Promise<string | null> {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		const match = devices.find((device) => device.kind === 'audioinput' && isVirtmicDeviceLabel(device.label));
		if (!match) {
			logger.warn('virtmic device not present in enumerateDevices output', {
				audioInputLabels: devices
					.filter((device) => device.kind === 'audioinput')
					.map((device) => device.label || '<empty>'),
			});
			return null;
		}
		return match.deviceId;
	} catch (error) {
		logger.warn('enumerateDevices failed while looking up virtmic device', {error});
		return null;
	}
}
function attachVirtmicCleanup(stream: MediaStream): void {
	const videoTrack = stream.getVideoTracks()[0];
	if (!videoTrack) return;
	const cleanup = (): void => {
		void getElectronAPI()?.virtmic?.stop();
		videoTrack.removeEventListener('ended', cleanup);
	};
	videoTrack.addEventListener('ended', cleanup);
}
function removeDisplayAudioTracks(stream: MediaStream): void {
	for (const existingAudio of stream.getAudioTracks()) {
		stream.removeTrack(existingAudio);
		try {
			existingAudio.stop();
		} catch {}
	}
}
function stopStreamTracks(stream: MediaStream): void {
	for (const track of stream.getTracks()) {
		try {
			track.stop();
		} catch {}
	}
}
export async function captureVirtmicAudioTrack(): Promise<MediaStreamTrack | null> {
	if (!isLinuxDesktop()) return null;
	const deviceId = await findVirtmicDeviceId();
	if (!deviceId) {
		logger.info('virtmic device not present; cannot capture mid-stream audio substitution');
		return null;
	}
	let virtAudio: MediaStream;
	try {
		virtAudio = await navigator.mediaDevices.getUserMedia({
			audio: {
				deviceId: {exact: deviceId},
				autoGainControl: false,
				echoCancellation: false,
				noiseSuppression: false,
				channelCount: 2,
				sampleRate: VIRTMIC_SAMPLE_RATE,
				sampleSize: 16,
			},
		});
	} catch (error) {
		logger.warn('Failed to capture virtmic audio for mid-stream substitution', {error});
		return null;
	}
	const virtTrack = virtAudio.getAudioTracks()[0];
	if (!virtTrack) {
		logger.warn('virtmic getUserMedia returned no audio tracks');
		stopStreamTracks(virtAudio);
		return null;
	}
	return virtTrack;
}
async function substituteAudioTrack(stream: MediaStream): Promise<void> {
	const deviceId = await findVirtmicDeviceId();
	if (!deviceId) {
		logger.info('virtmic device not present after capture; removing native display audio to avoid echo');
		removeDisplayAudioTracks(stream);
		void getElectronAPI()?.virtmic?.stop();
		return;
	}
	let virtAudio: MediaStream;
	try {
		virtAudio = await navigator.mediaDevices.getUserMedia({
			audio: {
				deviceId: {exact: deviceId},
				autoGainControl: false,
				echoCancellation: false,
				noiseSuppression: false,
				channelCount: 2,
				sampleRate: VIRTMIC_SAMPLE_RATE,
				sampleSize: 16,
			},
		});
	} catch (error) {
		logger.warn('Failed to capture virtmic audio; removing native display audio to avoid echo', {error});
		removeDisplayAudioTracks(stream);
		void getElectronAPI()?.virtmic?.stop();
		return;
	}
	removeDisplayAudioTracks(stream);
	const virtTrack = virtAudio.getAudioTracks()[0];
	if (!virtTrack) {
		logger.warn('virtmic getUserMedia returned no audio tracks');
		stopStreamTracks(virtAudio);
		void getElectronAPI()?.virtmic?.stop();
		return;
	}
	stream.addTrack(virtTrack);
}
function installPatchIfNeeded(): void {
	if (patched) return;
	if (!isLinuxDesktop()) return;
	if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) return;
	const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
	navigator.mediaDevices.getDisplayMedia = async function patchedGetDisplayMedia(
		this: MediaDevices,
		constraints?: DisplayMediaStreamOptions,
	): Promise<MediaStream> {
		const captureArmToken = armedToken;
		const stream = await original(constraints);
		if (captureArmToken && armedToken === captureArmToken) {
			armedToken = null;
			try {
				await substituteAudioTrack(stream);
				attachVirtmicCleanup(stream);
			} catch (error) {
				logger.warn('virtmic audio substitution failed', {error});
			}
		}
		return stream;
	};
	patched = true;
}
export function armVirtmicForNextCapture(): void {
	installPatchIfNeeded();
	armedToken = Symbol('virtmic-arm');
}
export function disarmVirtmic(): void {
	armedToken = null;
}
let availabilityCache: Promise<boolean> | null = null;
export function isVirtmicAvailable(): Promise<boolean> {
	if (!isLinuxDesktop()) return Promise.resolve(false);
	if (!availabilityCache) {
		const electronApi = getElectronAPI();
		availabilityCache = electronApi?.virtmic
			? electronApi.virtmic
					.getAvailability()
					.then((status) => status.available)
					.catch(() => false)
			: Promise.resolve(false);
	}
	return availabilityCache;
}
export function resetVirtmicAvailabilityCache(): void {
	availabilityCache = null;
}
